/**
 * Dexie-backed list of installed Charging Profiles for the current station.
 * Refreshed on demand by the Charging Profiles tab; the tab also calls
 * `add` and `remove` directly when the user clicks template buttons or
 * Delete on a row.
 *
 * `findNextId` returns max(id) + 1 so the four-template starter set picks
 * non-colliding ids without the UI having to ask the user.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { ChargingProfileRepositoryDexie } from '@/persistence/dexie/repositories';
import type { ChargingProfileSnapshot } from '@/domain';
import type { ChargingProfileId, StationId } from '@/domain/types';

export interface ChargingProfilesStoreState {
  readonly entries: readonly ChargingProfileSnapshot[];
  readonly status: 'idle' | 'loading' | 'ready';
}

export interface ChargingProfilesStoreActions {
  refresh(stationId: StationId): Promise<void>;
  add(stationId: StationId, snap: ChargingProfileSnapshot): Promise<void>;
  remove(stationId: StationId, id: ChargingProfileId): Promise<void>;
  clear(stationId: StationId): Promise<void>;
  findNextId(): number;
}

export type ChargingProfilesStore = ChargingProfilesStoreState & ChargingProfilesStoreActions;

const repo = new ChargingProfileRepositoryDexie(db);

export const useChargingProfilesStore = create<ChargingProfilesStore>((set, get) => ({
  entries: [],
  status: 'idle',

  async refresh(stationId: StationId): Promise<void> {
    set({ status: 'loading' });
    const entries = await repo.byStation(stationId);
    set({ entries, status: 'ready' });
  },

  async add(stationId: StationId, snap: ChargingProfileSnapshot): Promise<void> {
    await repo.upsert(stationId, snap);
    const entries = await repo.byStation(stationId);
    set({ entries });
  },

  async remove(stationId: StationId, id: ChargingProfileId): Promise<void> {
    await repo.delete(stationId, id);
    const entries = await repo.byStation(stationId);
    set({ entries });
  },

  async clear(stationId: StationId): Promise<void> {
    await repo.clear(stationId);
    set({ entries: [] });
  },

  findNextId(): number {
    const max = get().entries.reduce<number>((m, p) => Math.max(m, Number(p.id)), 0);
    return max + 1;
  },
}));
